import { motion } from "framer-motion";
import { MapPin, Building2, Disc3, Castle } from "lucide-react";

const CLUBS = [
  { name: "L'Arc-en-Ciel", city: "Alençon (61)" },
  { name: "Le Bayokos", city: "Alençon (61)" },
  { name: "Le Lipstick", city: "Livarot (61)" },
  { name: "Le Thamengo", city: "Argentan (61)" },
  { name: "Le Trianon", city: "Eure (27)" },
];

const VENUES_ORNE = [
  "Domaine de la Laumondière – Bagnoles",
  "Le Damier Normand – Essay",
  "La Maréchalerie – Camembert",
  "La Grange de Villeron – La Ferté-Fresnel",
  "Domaine de la Touche – Saint-Denis/Sarthon",
  "Domaine du Bois d'Avoine – Avoisne",
  "Hameau de la Fouquière – Joué-du-Bois",
  "Casino de Bagnoles-de-l'Orne",
  "Domaine de la Cour – Aunou-sur-Orne",
  "Château de Villiers – Essay",
  "Le Clos-des-Cheminées – Longny-au-Perche",
];

const VENUES_SARTHE = [
  "Château de Cheronne – Tuffé",
  "Abbaye de Champagne – Rouez",
  "Haras de la Potardière – Crosmières",
  "Château d'Oyré – Clermont-Créans",
  "Château de Montbraye – Parigné-l'Évêque",
  "Domaine de la Trigalière",
  "La Métairie René",
  "Gîte du Grand Cerf – Yvré-le-Polin",
  "La Ferme du Petit Etaloir – Fatines",
  "La Terrasse des Oliviers – Mansigné",
];

export const Venues = () => {
  return (
    <section
      id="venues"
      data-testid="venues-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,108,255,0.08),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow inline-flex items-center gap-2">
            <MapPin size={13} /> Lieux & domaines
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            J'ai eu la chance d'animer dans les{" "}
            <span className="gold-text-gradient">plus beaux lieux</span> du Grand Ouest.
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Châteaux, domaines, abbayes, discothèques mythiques, casinos, salles
            communales… Plus de 100 lieux partenaires, et une connaissance fine de
            chaque acoustique pour une prestation parfaite dès la première minute.
          </p>
        </div>

        {/* Discothèques résidences */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="glass rounded-2xl p-7 md:p-9 mb-6"
          data-testid="venues-clubs"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#2D6CFF]/15 text-[#4A86FF]">
              <Disc3 size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#4A86FF] font-semibold">
                Résidences
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white">
                Discothèques où j'ai mixé en résidence
              </h3>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5">
            {CLUBS.map((c) => (
              <div
                key={c.name}
                data-testid={`club-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-2.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-[#4A86FF]/40 transition group"
              >
                <span className="text-sm font-semibold text-white">{c.name}</span>
                <span className="text-xs text-zinc-400 ml-2">· {c.city}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Domaines & châteaux */}
        <div className="grid md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-7 md:p-8"
            data-testid="venues-orne"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37]">
                <Castle size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                  Orne (61)
                </div>
                <h3 className="text-xl font-bold text-white">Domaines & lieux de réception</h3>
              </div>
            </div>
            <ul className="space-y-2.5">
              {VENUES_ORNE.map((v) => (
                <li
                  key={v}
                  className="text-sm text-zinc-300 flex items-start gap-2 leading-snug"
                >
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-7 md:p-8"
            data-testid="venues-sarthe"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D4AF37]/15 text-[#D4AF37]">
                <Building2 size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                  Sarthe (72)
                </div>
                <h3 className="text-xl font-bold text-white">Châteaux & abbayes</h3>
              </div>
            </div>
            <ul className="space-y-2.5">
              {VENUES_SARTHE.map((v) => (
                <li
                  key={v}
                  className="text-sm text-zinc-300 flex items-start gap-2 leading-snug"
                >
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Zone élargie */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-2xl p-7 md:p-8 border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/[0.05] to-transparent"
        >
          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mb-2">
                Zone d'intervention élargie
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                Tout le Grand Ouest. Et bien plus.
              </h3>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Basé à <span className="text-white font-medium">La Chapelle-près-Sées (61500)</span>,
                je me déplace partout en France pour vos événements. Devis selon le lieu.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-2">
              {[
                "Orne",
                "Sarthe",
                "Calvados",
                "Mayenne",
                "Eure",
                "Eure-et-Loir",
                "Seine-Maritime",
                "Ille-et-Vilaine",
                "Indre-et-Loire",
                "Maine-et-Loire",
                "Île-de-France",
              ].map((z) => (
                <span
                  key={z}
                  data-testid={`zone-${z.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="text-[11px] tracking-[0.18em] uppercase text-zinc-200 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
